import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
 
function FAIDocument() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [bubbles, setBubbles] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [inspectorName, setInspectorName] = useState('');
  const [inspectionDate, setInspectionDate] = useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = useState(false);
  const [actuals, setActuals] = useState({});
 
  useEffect(() => {
    const load = async () => {
      try {
        const docRef = doc(db, 'projects', projectId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setProject(data);
          setBubbles(data.bubbles || []);
          setCompanyName(data.companyName || '');
          setInspectorName(data.inspectorName || '');
          if (data.inspectionDate) setInspectionDate(data.inspectionDate);
          setActuals(data.actuals || {});
        }
      } catch (err) {
        console.error(err);
      }
    };
    load();
  }, [projectId]);
 
  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'projects', projectId), {
        companyName, inspectorName, inspectionDate, actuals,
        bubbles: bubbles
      });
      alert('FAI saved successfully!');
    } catch (err) {
      console.error(err);
      alert('Error saving.');
    }
    setSaving(false);
  };
 
  const handlePrint = () => window.print();
  const setActual = (id, val) => setActuals(prev => ({ ...prev, [id]: val }));
  const setStatus = (id, val) => setBubbles(prev => prev.map(b => b.id === id ? { ...b, status: val } : b));
 
  const passCount = bubbles.filter(b => b.status === 'Pass').length;
  const failCount = bubbles.filter(b => b.status === 'Fail').length;
  const pendingCount = bubbles.filter(b => b.status === 'Pending').length;
 
  return (
    <div style={styles.page}>
      {/* SIDEBAR */}
      <div style={styles.sidebar}>
        <div style={styles.logo}>
          <span style={styles.logoText}>M<span style={styles.logoBlue}>I</span>A</span>
          <div style={styles.logoSub}>Manufacturing<br />Inspection Assistant</div>
        </div>
        <nav style={styles.nav}>
          <div style={styles.navItem} onClick={() => navigate('/')}><span>⬆</span> Upload Print</div>
          <div style={styles.navItem} onClick={() => navigate(`/review/${projectId}`)}><span>◎</span> Review Bubbles</div>
          <div style={{ ...styles.navItem, ...styles.navActive }}><span>☰</span> FAI Document</div>
          <div style={styles.navItem} onClick={() => navigate(`/final/${projectId}`)}><span>✓</span> Final Inspection</div>
          <div style={styles.navItem} onClick={() => navigate(`/exports/${projectId}`)}><span>↓</span> Exports</div>
        </nav>
      </div>
 
      {/* MAIN */}
      <div style={styles.main}>
 
        {/* TOP BAR */}
        <div style={styles.topbar}>
          <div style={styles.stepperRow}>
            <div style={styles.stepDone}>✓ Upload PDF</div>
            <div style={styles.stepLine}></div>
            <div style={styles.stepDone}>✓ Analyze Print</div>
            <div style={styles.stepLine}></div>
            <div style={styles.stepDone}>✓ Review Bubbles</div>
            <div style={styles.stepLine}></div>
            <div style={styles.stepActive}>4 Generate FAI</div>
            <div style={styles.stepLineIdle}></div>
            <div style={styles.stepIdle}>5 Final Inspection</div>
          </div>
          <div style={styles.topbarRight}>
            <button style={styles.btnOutline} onClick={() => navigate(`/review/${projectId}`)}>← Back to Bubbles</button>
            <button style={styles.btnOutline} onClick={handlePrint}>🖨 Print</button>
            <button style={styles.btnPrimary} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : '💾 Save FAI'}</button>
          </div>
        </div>
 
        {/* CONTENT */}
        <div style={styles.content}>
 
          {/* SUMMARY CARDS */}
          <div style={styles.summaryRow}>
            <div style={{ ...styles.summaryCard, borderTop: '3px solid #2563EB' }}>
              <div style={styles.summaryNum}>{bubbles.length}</div>
              <div style={styles.summaryLbl}>Total Characteristics</div>
            </div>
            <div style={{ ...styles.summaryCard, borderTop: '3px solid #16a34a' }}>
              <div style={{ ...styles.summaryNum, color: '#16a34a' }}>{passCount}</div>
              <div style={styles.summaryLbl}>Passed</div>
            </div>
            <div style={{ ...styles.summaryCard, borderTop: '3px solid #dc2626' }}>
              <div style={{ ...styles.summaryNum, color: '#dc2626' }}>{failCount}</div>
              <div style={styles.summaryLbl}>Failed</div>
            </div>
            <div style={{ ...styles.summaryCard, borderTop: '3px solid #f59e0b' }}>
              <div style={{ ...styles.summaryNum, color: '#f59e0b' }}>{pendingCount}</div>
              <div style={styles.summaryLbl}>Pending</div>
            </div>
          </div>
 
          {/* FAI DOCUMENT */}
          <div style={styles.document} id="fai-document">
 
            {/* DOCUMENT HEADER */}
            <div style={styles.docHeader}>
              <div style={styles.docHeaderLeft}>
                <div style={styles.docCompany}>
                  <input
                    style={styles.companyInput}
                    placeholder="Company Name"
                    value={companyName}
                    onChange={e => setCompanyName(e.target.value)}
                  />
                </div>
                <div style={styles.docReportType}>FIRST ARTICLE INSPECTION REPORT</div>
              </div>
              <div style={styles.docHeaderRight}>
                <div style={styles.miaWatermark}>Generated by MIA</div>
              </div>
            </div>
 
            {/* PART INFO */}
            <div style={styles.partInfo}>
              <div style={styles.partInfoGrid}>
                <div style={styles.infoCell}>
                  <div style={styles.infoCellLabel}>Part Number</div>
                  <div style={styles.infoCellValue}>{project?.partNumber || '—'}</div>
                </div>
                <div style={styles.infoCell}>
                  <div style={styles.infoCellLabel}>Revision</div>
                  <div style={styles.infoCellValue}>{project?.revision || '—'}</div>
                </div>
                <div style={styles.infoCell}>
                  <div style={styles.infoCellLabel}>Drawing Number</div>
                  <div style={styles.infoCellValue}>{project?.drawingNumber || '—'}</div>
                </div>
                <div style={styles.infoCell}>
                  <div style={styles.infoCellLabel}>Material</div>
                  <div style={styles.infoCellValue}>{project?.material || '—'}</div>
                </div>
                <div style={styles.infoCell}>
                  <div style={styles.infoCellLabel}>Finish</div>
                  <div style={styles.infoCellValue}>{project?.finish || '—'}</div>
                </div>
                <div style={styles.infoCell}>
                  <div style={styles.infoCellLabel}>Units</div>
                  <div style={styles.infoCellValue}>{project?.units || 'INCH'}</div>
                </div>
                <div style={styles.infoCell}>
                  <div style={styles.infoCellLabel}>Inspector</div>
                  <input
                    style={styles.infoInput}
                    placeholder="Inspector name"
                    value={inspectorName}
                    onChange={e => setInspectorName(e.target.value)}
                  />
                </div>
                <div style={styles.infoCell}>
                  <div style={styles.infoCellLabel}>Inspection Date</div>
                  <input
                    type="date"
                    style={styles.infoInput}
                    value={inspectionDate}
                    onChange={e => setInspectionDate(e.target.value)}
                  />
                </div>
                <div style={styles.infoCell}>
                  <div style={styles.infoCellLabel}>Filename</div>
                  <div style={styles.infoCellValue}>{project?.fileName || '—'}</div>
                </div>
              </div>
            </div>
 
            {/* INSPECTION TABLE */}
            <table style={styles.faiTable}>
              <thead>
                <tr style={styles.faiHeadRow}>
                  <th style={{ ...styles.faiTh, width: '44px' }}>Bubble #</th>
                  <th style={styles.faiTh}>Requirement</th>
                  <th style={{ ...styles.faiTh, width: '80px' }}>Nominal</th>
                  <th style={{ ...styles.faiTh, width: '80px' }}>Tolerance</th>
                  <th style={{ ...styles.faiTh, width: '100px' }}>Feature Type</th>
                  <th style={{ ...styles.faiTh, width: '120px' }}>Insp. Method</th>
                  <th style={{ ...styles.faiTh, width: '100px' }}>Actual</th>
                  <th style={{ ...styles.faiTh, width: '100px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {bubbles.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={styles.emptyCell}>
                      No bubbles yet — go back to Review Bubbles to add inspection characteristics
                    </td>
                  </tr>
                ) : (
                  bubbles.map((b, i) => (
                    <tr key={b.id} style={{ ...styles.faiRow, background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                      <td style={{ ...styles.faiTd, textAlign: 'center' }}>
                        <div style={{
                          ...styles.bubbleNum,
                          background: b.status === 'Pass' ? '#16a34a' : b.status === 'Fail' ? '#dc2626' : '#2563EB'
                        }}>{b.number}</div>
                      </td>
                      <td style={styles.faiTd}>{b.requirement || <span style={styles.empty}>—</span>}</td>
                      <td style={{ ...styles.faiTd, fontFamily: 'monospace', textAlign: 'center' }}>{b.nominal || '—'}</td>
                      <td style={{ ...styles.faiTd, fontFamily: 'monospace', textAlign: 'center' }}>{b.tolerance || '—'}</td>
                      <td style={{ ...styles.faiTd, textAlign: 'center', fontSize: '11px', color: '#6b7280' }}>{b.featureType}</td>
                      <td style={{ ...styles.faiTd, textAlign: 'center', fontSize: '11px', color: '#6b7280' }}>{b.inspectionMethod}</td>
                      <td style={{ ...styles.faiTd, textAlign: 'center' }}>
                        <input
                          style={styles.actualInput}
                          placeholder="Enter"
                          value={actuals[b.id] || ''}
                          onChange={e => setActual(b.id, e.target.value)}
                        />
                      </td>
                      <td style={{ ...styles.faiTd, textAlign: 'center' }}>
                        <select
                          style={{
                            border: '1px solid',
                            borderRadius: '6px',
                            padding: '4px 6px',
                            fontSize: '11px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                            outline: 'none',
                            width: '88px',
                            background: b.status === 'Pass' ? '#dcfce7' : b.status === 'Fail' ? '#fef2f2' : b.status === 'N/A' ? '#f3f4f6' : '#fefce8',
                            color: b.status === 'Pass' ? '#16a34a' : b.status === 'Fail' ? '#dc2626' : b.status === 'N/A' ? '#6b7280' : '#92400e',
                            borderColor: b.status === 'Pass' ? '#86efac' : b.status === 'Fail' ? '#fca5a5' : '#e5e7eb'
                          }}
                          value={b.status}
                          onChange={e => setStatus(b.id, e.target.value)}
                        >
                          <option>Pending</option>
                          <option>Pass</option>
                          <option>Fail</option>
                          <option>N/A</option>
                        </select>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
 
            {/* SIGNATURE BLOCK */}
            <div style={styles.signatureBlock}>
              <div style={styles.sigBox}>
                <div style={styles.sigLine}></div>
                <div style={styles.sigLabel}>Inspector Signature</div>
              </div>
              <div style={styles.sigBox}>
                <div style={styles.sigLine}></div>
                <div style={styles.sigLabel}>Date</div>
              </div>
              <div style={styles.sigBox}>
                <div style={styles.sigLine}></div>
                <div style={styles.sigLabel}>Quality Manager Signature</div>
              </div>
              <div style={styles.sigBox}>
                <div style={styles.sigLine}></div>
                <div style={styles.sigLabel}>Date</div>
              </div>
            </div>
 
            {/* DOCUMENT FOOTER */}
            <div style={styles.docFooter}>
              <div>Generated by MIA — Manufacturing Inspection Assistant</div>
              <div>Page 1 of 1 · {new Date().toLocaleDateString()}</div>
            </div>
          </div>
 
          {/* NEXT STEP */}
          <div style={styles.nextStep}>
            <div style={styles.nextStepText}>FAI complete? Move to Final Inspection to record actual measurements and close out the report.</div>
            <button style={styles.btnPrimary} onClick={() => navigate(`/final/${projectId}`)}>
              Continue to Final Inspection →
            </button>
          </div>
        </div>
      </div>
 
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #fai-document, #fai-document * { visibility: visible; }
          #fai-document { position: absolute; left: 0; top: 0; width: 100%; }
        }
      `}</style>
    </div>
  );
}
 
const styles = {
  page: { display: 'flex', height: '100vh', background: '#f0f2f5', fontFamily: 'Inter, sans-serif', overflow: 'hidden' },
  sidebar: { width: '210px', background: '#0d1b2a', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  logo: { padding: '24px 18px 20px', borderBottom: '1px solid #1a2a3a', display: 'flex', flexDirection: 'column', gap: '4px' },
  logoText: { fontSize: '32px', fontWeight: '800', color: '#fff', letterSpacing: '-1px', lineHeight: 1 },
  logoBlue: { color: '#3b82f6' },
  logoSub: { fontSize: '10px', color: '#475569', lineHeight: '1.4', marginTop: '4px' },
  nav: { padding: '12px 0', flex: 1 },
  navItem: { display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 18px', fontSize: '13px', color: '#64748b', cursor: 'pointer' },
  navActive: { color: '#e2e8f0', fontWeight: '500', borderLeft: '3px solid #3b82f6', paddingLeft: '15px' },
  main: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  topbar: { height: '52px', background: '#fff', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', padding: '0 20px', flexShrink: 0 },
  stepperRow: { display: 'flex', alignItems: 'center', gap: '0', flex: 1 },
  stepDone: { fontSize: '12px', fontWeight: '500', color: '#2563EB', whiteSpace: 'nowrap' },
  stepActive: { fontSize: '12px', fontWeight: '600', color: '#111827', whiteSpace: 'nowrap' },
  stepIdle: { fontSize: '12px', color: '#9ca3af', whiteSpace: 'nowrap' },
  stepLine: { width: '32px', height: '1.5px', background: '#2563EB', margin: '0 6px' },
  stepLineIdle: { width: '32px', height: '1.5px', background: '#e5e7eb', margin: '0 6px' },
  topbarRight: { display: 'flex', gap: '8px' },
  btnOutline: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: '7px', padding: '7px 14px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', color: '#374151' },
  btnPrimary: { background: '#2563EB', border: 'none', borderRadius: '7px', padding: '7px 16px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', color: '#fff' },
  content: { flex: 1, overflowY: 'auto', padding: '24px 28px' },
  summaryRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' },
  summaryCard: { background: '#fff', borderRadius: '10px', padding: '16px', border: '1px solid #e5e7eb' },
  summaryNum: { fontSize: '28px', fontWeight: '800', color: '#111827', lineHeight: 1 },
  summaryLbl: { fontSize: '12px', color: '#9ca3af', marginTop: '4px', fontWeight: '500' },
  document: { background: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb', overflow: 'hidden', marginBottom: '20px' },
  docHeader: { background: '#0B1220', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  docHeaderLeft: { display: 'flex', flexDirection: 'column', gap: '4px' },
  docCompany: {},
  companyInput: { background: 'transparent', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.2)', color: '#fff', fontSize: '18px', fontWeight: '700', fontFamily: 'Inter, sans-serif', outline: 'none', padding: '2px 0', width: '280px' },
  docReportType: { fontSize: '11px', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: '4px' },
  docHeaderRight: {},
  miaWatermark: { fontSize: '11px', color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' },
  partInfo: { padding: '16px 24px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' },
  partInfoGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' },
  infoCell: { display: 'flex', flexDirection: 'column', gap: '3px' },
  infoCellLabel: { fontSize: '9.5px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em' },
  infoCellValue: { fontSize: '13px', fontWeight: '600', color: '#111827' },
  infoInput: { border: 'none', borderBottom: '1px solid #e5e7eb', background: 'transparent', fontSize: '13px', fontWeight: '600', color: '#111827', fontFamily: 'inherit', outline: 'none', padding: '2px 0' },
  faiTable: { width: '100%', borderCollapse: 'collapse' },
  faiHeadRow: { background: '#f1f5f9' },
  faiTh: { padding: '10px 12px', textAlign: 'left', fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0' },
  faiRow: { borderBottom: '1px solid #f1f5f9' },
  faiTd: { padding: '9px 12px', fontSize: '12.5px', color: '#374151', borderRight: '1px solid #f1f5f9', verticalAlign: 'middle' },
  bubbleNum: { width: '22px', height: '22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '700', color: '#fff', margin: '0 auto' },
  actualInput: { width: '80px', border: '1px solid #e5e7eb', borderRadius: '5px', padding: '4px 8px', fontSize: '12px', fontFamily: 'monospace', textAlign: 'center', outline: 'none', background: '#fff', color: '#111827' },
  emptyCell: { padding: '32px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' },
  empty: { color: '#d1d5db' },
  signatureBlock: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '20px', borderTop: '1px solid #e5e7eb', padding: '24px' },
  sigBox: { display: 'flex', flexDirection: 'column', gap: '6px' },
  sigLine: { height: '1px', background: '#374151', width: '100%' },
  sigLabel: { fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em' },
  docFooter: { background: '#f9fafb', borderTop: '1px solid #e5e7eb', padding: '10px 24px', display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#9ca3af' },
  nextStep: { background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' },
  nextStepText: { fontSize: '13px', color: '#1e40af', fontWeight: '500' },
};
 
export default FAIDocument;